/**
 * @file        src/modals/CancellationPolicyModal.jsx
 * @description Cancellation-policy popup for a room rate. Opened via
 *              nh:open-modal { name: 'cancellation', payload: { cancellation,
 *              roomName } } where `cancellation` is the v3 option block
 *              { isRefundable, penalties:[{ from, to, amount }] }.
 *              Shows a refundable/non-refundable banner with a Now → Check-in
 *              timeline and a table of penalty windows + charges. All values are
 *              live supplier data — nothing is fabricated.
 * @author      Kinetika Labs
 * @created     2026-06-24
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row sat under the toolbar. `svh` is
 *                          the always-visible height. Desktop is unaffected (svh == vh).
 */
import { useEffect } from 'react';
import { useBooking } from '../store/booking.store.js';
import { inr } from '../lib/format.js';

// Defensive readers — the supplier penalty objects use {from,to,amount} but we
// fall back to common alternates so a field rename never blanks the table.
const pFrom = (p) => p?.from ?? p?.fromDate ?? p?.startDate ?? null;
const pTo = (p) => p?.to ?? p?.toDate ?? p?.endDate ?? null;
const pAmt = (p) => p?.amount ?? p?.charge ?? p?.amt ?? p?.cancellationCharge ?? null;

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function fmtCheckIn(v) {
  if (!v) return 'Check-in';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const day = d.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  const mon = d.toLocaleString('en-US', { month: 'short' });
  return `${day}${suffix} ${mon}`;
}

export default function CancellationPolicyModal({ open, onClose, payload }) {
  const checkIn = useBooking((s) => s.hotelSearch?.checkIn);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const cancellation = payload?.cancellation || {};
  const roomName = payload?.roomName || 'Room';
  const refundable = Boolean(cancellation.isRefundable);
  const penalties = Array.isArray(cancellation.penalties) ? cancellation.penalties : [];

  // Policy notes come from the supplier when present (policy text + any per-
  // window comments). Nothing is hardcoded — if the supplier sends none, the
  // notes row is omitted entirely.
  const notes = [];
  [cancellation.policyText, cancellation.comments, cancellation.instructions, cancellation.remarks, cancellation.note]
    .forEach((t) => { if (typeof t === 'string' && t.trim()) notes.push(t.trim()); });
  penalties.forEach((p) => {
    const c = p?.comment ?? p?.comments ?? p?.remarks ?? p?.note;
    if (typeof c === 'string' && c.trim() && !notes.includes(c.trim())) notes.push(c.trim());
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel p-6 no-scrollbar">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer" aria-label="Close">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>

        <h2 className="font-display text-xl font-extrabold pr-10">{roomName}</h2>

        {/* policy summary */}
        <p className="text-sm font-semibold text-ink mt-5 mb-2">Cancellation Policy</p>
        <p className="flex items-center gap-2 text-sm">
          <svg viewBox="0 0 24 24" className={`w-4 h-4 ${refundable ? 'text-lime' : 'text-dangerink'}`}><path fill="currentColor" d="m9 16.2-3.5-3.5L4 14.2 9 19l11-11-1.4-1.4z" /></svg>
          <span className="text-muted">{refundable ? 'Refundable (see windows below)' : 'Non Refundable'}</span>
        </p>

        {/* banner */}
        <div className={`mt-4 rounded-xl px-5 py-3 text-center font-bold ${refundable ? 'bg-lime/20 text-lime border border-lime/30' : 'bg-danger/20 text-dangerink border border-danger/30'}`}>
          {refundable ? 'Free Cancellation Available' : 'Non-Refundable'}
        </div>

        {/* timeline */}
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="text-muted">Now</span>
          <div className="text-right">
            <p className="text-ink font-semibold">{fmtCheckIn(checkIn)}</p>
            <p className="text-muted">Check-in</p>
          </div>
        </div>

        {/* penalty table */}
        {penalties.length > 0 ? (
          <>
            <p className="text-sm font-semibold text-ink mt-6 mb-2">
              Cancellation post that will be subject to a fee as follows
            </p>
            <div className="rounded-xl border border-line overflow-hidden">
              <div className="grid grid-cols-3 bg-night/50 text-[11px] font-semibold uppercase tracking-wide text-muted">
                <span className="px-3 py-2.5">Cancellation On or After</span>
                <span className="px-3 py-2.5 border-l border-line">Cancellation On or Before</span>
                <span className="px-3 py-2.5 border-l border-line text-right">Charges / Comments</span>
              </div>
              {penalties.map((p, i) => (
                <div key={i} className="grid grid-cols-3 text-sm border-t border-line">
                  <span className="px-3 py-3 text-muted">{fmtDate(pFrom(p))}</span>
                  <span className="px-3 py-3 text-muted border-l border-line">{fmtDate(pTo(p))}</span>
                  <span className="px-3 py-3 font-semibold text-ink border-l border-line text-right">
                    {pAmt(p) != null ? inr(Number(pAmt(p))) : '—'}
                  </span>
                </div>
              ))}
              {notes.length > 0 && (
                <div className="border-t border-line px-3 py-3 text-xs text-muted text-center space-y-1.5">
                  {notes.map((n, i) => <p key={i}>{n}</p>)}
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted mt-6">
            {refundable
              ? 'Cancellation charge details are not available for this rate.'
              : 'This booking cannot be modified, and no refund will be given if you cancel it.'}
          </p>
        )}
      </div>
    </div>
  );
}
